import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { IntradayCandle } from '../services/stockSignalsService';

interface IntradayChartProps {
  candles: IntradayCandle[];
  entryPrice: number;
  targetPrice: number;
  stopLoss: number;
  symbol: string;
}

// All chart times rendered in ET — the candles span market hours and the
// "all times Eastern" rule applies. Without timeZone, formatters fall back to
// browser-local which can drift the labels by a full hour for users outside
// US/Eastern.
const ET = 'America/New_York';

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString('en-US', { timeZone: ET, month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' });
  } catch {
    return ts;
  }
}

function formatXTick(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    // Include hour:minute — without it, multiple intraday ticks on the same
    // calendar day collapse to identical "5/1" labels (same bug as the
    // OptionPremiumChart fix). Hourly candles need at least the hour to
    // disambiguate.
    const date = d.toLocaleDateString('en-US', { timeZone: ET, month: 'numeric', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date} ${time}`;
  } catch {
    return '';
  }
}

class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '0.75rem 1.5rem',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          fontSize: '0.8em',
          color: '#64748b'
        }}>
          Chart unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

const IntradayChartInner: React.FC<IntradayChartProps> = ({
  candles,
  entryPrice,
  targetPrice,
  stopLoss,
  symbol,
}) => {
  if (!candles || candles.length === 0) return null;

  const gradientId = `areaGrad-${symbol}`;

  const data = candles.map(c => ({
    ts: c.timestamp,
    close: typeof c.close === 'number' && isFinite(c.close) ? c.close : 0,
  }));

  const closes = data.map(d => d.close).filter(c => c > 0);
  if (closes.length === 0) return null;

  const minClose = Math.min(...closes, stopLoss);
  const maxClose = Math.max(...closes, targetPrice);
  const range = maxClose - minClose;
  const padding = range > 0 ? range * 0.08 : maxClose * 0.02;
  const yMin = Math.floor((minClose - padding) * 100) / 100;
  const yMax = Math.ceil((maxClose + padding) * 100) / 100;

  // Show ~6 x-axis ticks spread across data
  const tickIndices: number[] = [];
  const step = Math.max(1, Math.floor(data.length / 6));
  for (let i = 0; i < data.length; i += step) tickIndices.push(i);
  const ticks = tickIndices.map(i => data[i].ts);

  // Determine area color based on last close vs entry
  const lastClose = closes[closes.length - 1];
  const isProfit = lastClose >= entryPrice;
  const areaColor = isProfit ? '#22c55e' : '#ef4444';

  return (
    <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontSize: '0.8em', color: '#94a3b8', marginBottom: '0.5rem' }}>
        Intraday Price (1hr candles)
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={areaColor} stopOpacity={0.3} />
              <stop offset="100%" stopColor={areaColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="ts"
            ticks={ticks}
            tickFormatter={formatXTick}
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            tickLine={false}
          />
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `$${v.toFixed(0)}`}
            width={50}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1e293b',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '6px',
              fontSize: '0.85em',
            }}
            labelFormatter={formatTimestamp}
            formatter={(value: number | undefined) => [`$${(value ?? 0).toFixed(2)}`, 'Close']}
          />
          <ReferenceLine
            y={entryPrice}
            stroke="#3b82f6"
            strokeDasharray="4 4"
            label={{ value: 'Entry', fill: '#3b82f6', fontSize: 10, position: 'left' }}
          />
          <ReferenceLine
            y={targetPrice}
            stroke="#22c55e"
            strokeDasharray="4 4"
            label={{ value: 'Target', fill: '#22c55e', fontSize: 10, position: 'left' }}
          />
          <ReferenceLine
            y={stopLoss}
            stroke="#ef4444"
            strokeDasharray="4 4"
            label={{ value: 'Stop', fill: '#ef4444', fontSize: 10, position: 'left' }}
          />
          <Area
            type="monotone"
            dataKey="close"
            stroke={areaColor}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 3, fill: areaColor }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const IntradayChart: React.FC<IntradayChartProps> = (props) => (
  <ChartErrorBoundary>
    <IntradayChartInner {...props} />
  </ChartErrorBoundary>
);
